/**
 * @fileoverview ARITA Library Animation
 *
 * Purpose: A light, stylised recreation of Morgan's ARITA precedent library for
 * the homepage integrations card ("ARITA precedents, built into workflows.").
 *
 * It mirrors the in-app "ARITA Master Library" screen: a chrome window titled
 * "ARITA Library" with a decorative search bar and a short, fixed list of
 * precedent cards (file icon, precedent name, an "ARITA" provenance badge + the
 * precedent type, and an "Add"/"Added" affordance).
 *
 * Motion is intentionally minimal: precedents are gently marked "Added" one at
 * a time on a slow loop (a soft crossfade of the Add → Added pill) to suggest
 * shared ARITA precedents flowing into the firm library. There is no scrolling.
 * Respects prefers-reduced-motion by resting on the fully-added state.
 *
 * The window is absolutely positioned (`inset-0`) so it fills the card's
 * placeholder without ever contributing to its height. Theming uses the global
 * semantic CSS variables so it adapts to light and dark mode.
 *
 * This is a marketing stylisation — no live data.
 *
 * Used by: IntegrationsSection (-sections.integrations.tsx)
 */

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, FileText, Library, Plus, Search } from "lucide-react";

/** A single ARITA precedent shown in the library. */
interface Precedent {
  /** Precedent name (mirrors the real ARITA library naming). */
  name: string;
  /** Precedent type — drives the muted type label. */
  type: "Form" | "Letter";
}

/** A short, fixed slice of the ARITA master library (names mirror the real one). */
const PRECEDENTS: Precedent[] = [
  { name: "F(ATO)B ATO Appointment Or Cessation", type: "Form" },
  { name: "F509 (CVL) Summary Of Affairs V1.0", type: "Form" },
  { name: "07190 (SBR) LTR Directors SBR Enquiry", type: "Letter" },
  { name: "17323 (SCVL) APP Directors' Declaration", type: "Form" },
  { name: "11901 LTR Sheriff Initial Notification V2.0", type: "Letter" },
] as const;

/** How long each precedent stays "Added" before the loop resets. */
const STEP_MS = 1100;
const RESET_HOLD_MS = 1600;

export function AritaLibraryAnimation() {
  const reducedMotion = useReducedMotion() ?? false;
  // Number of precedents currently marked "Added" (fills then resets on a loop).
  const [addedCount, setAddedCount] = useState(
    reducedMotion ? PRECEDENTS.length : 0,
  );

  useEffect(() => {
    if (reducedMotion) {
      setAddedCount(PRECEDENTS.length);
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    const tick = (next: number) => {
      setAddedCount(next);
      const done = next >= PRECEDENTS.length;
      timer = setTimeout(
        () => tick(done ? 0 : next + 1),
        done ? RESET_HOLD_MS : STEP_MS,
      );
    };
    timer = setTimeout(() => tick(1), STEP_MS);
    return () => clearTimeout(timer);
  }, [reducedMotion]);

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden rounded-lg border border-primary/10 bg-white shadow-sm dark:bg-card">
      {/* Title bar */}
      <div className="flex items-center gap-2 border-b border-primary/10 bg-primary/[0.03] px-3 py-2">
        <div className="flex gap-1.5">
          <span className="h-2 w-2 rounded-full bg-red-400/70" />
          <span className="h-2 w-2 rounded-full bg-amber-400/70" />
          <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
        </div>
        <span className="ml-1 flex items-center gap-1.5 text-[11px] font-semibold text-primary">
          <Library className="h-3 w-3" />
          ARITA Library
        </span>
        <span className="ml-auto rounded-md bg-primary/[0.06] px-1.5 py-0.5 text-[8.5px] font-semibold text-primary/55">
          225 available
        </span>
      </div>

      {/* Decorative search bar */}
      <div className="border-b border-primary/10 px-3 py-2">
        <div className="flex items-center gap-1.5 rounded-md border border-primary/10 bg-primary/[0.02] px-2 py-1.5">
          <Search className="h-3 w-3 shrink-0 text-primary/35" />
          <span className="text-[9px] font-medium text-primary/35">
            Search ARITA precedents…
          </span>
        </div>
      </div>

      {/* Fixed precedent list (no scroll) */}
      <div className="flex min-h-0 flex-1 flex-col justify-center gap-2 px-3 py-3">
        {PRECEDENTS.map((precedent, i) => (
          <PrecedentCard
            key={precedent.name}
            precedent={precedent}
            added={i < addedCount}
          />
        ))}
      </div>
    </div>
  );
}

/** A single precedent row, mirroring the ARITA library card. */
function PrecedentCard({
  precedent,
  added,
}: {
  precedent: Precedent;
  added: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-primary/10 bg-white px-2.5 py-2 dark:bg-card">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/[0.06] text-primary/55">
        <FileText className="h-3.5 w-3.5" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-[9.5px] font-semibold leading-tight text-foreground">
          {precedent.name}
        </p>
        <div className="mt-1 flex items-center gap-1.5">
          <span className="rounded-[3px] bg-blue-100 px-1 py-px text-[7px] font-semibold uppercase tracking-wider text-blue-700">
            ARITA
          </span>
          <span className="text-[8px] font-medium text-primary/40">
            {precedent.type}
          </span>
        </div>
      </div>

      {/* Add → Added: a soft crossfade is the only motion */}
      <span className="relative flex h-[18px] w-[52px] shrink-0 items-center justify-end">
        <AnimatePresence initial={false} mode="wait">
          {added ? (
            <motion.span
              key="added"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="flex items-center gap-0.5 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[8px] font-semibold text-emerald-700"
            >
              <Check className="h-2.5 w-2.5" />
              Added
            </motion.span>
          ) : (
            <motion.span
              key="add"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="flex items-center gap-0.5 rounded-md border border-primary/15 px-1.5 py-0.5 text-[8px] font-semibold text-primary/55"
            >
              <Plus className="h-2.5 w-2.5" />
              Add
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </div>
  );
}
